import { useState, useEffect } from "react";
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import MenuItem from "@mui/material/MenuItem";
import Snackbar from "@mui/material/Snackbar";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "layouts/authentication/components/Footer";
import { useGetControlsQuery, useUpdateControlsMutation } from "slices/addOnApi";

import TuneIcon from "@mui/icons-material/Tune";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import PercentIcon from "@mui/icons-material/Percent";
import FaceRetouchingNaturalIcon from "@mui/icons-material/FaceRetouchingNatural";
import SpaIcon from "@mui/icons-material/Spa";
import SaveIcon from "@mui/icons-material/Save";

// ── section header ────────────────────────────────────────────
const SectionHead = ({ icon, title, subtitle }) => (
  <Box display="flex" alignItems="center" gap={1.5} mb={2.5}>
    <Box sx={{ background: "#f3e8ff", borderRadius: 2, p: 1, display: "flex" }}>{icon}</Box>
    <Box>
      <Typography variant="h6" fontWeight={700} color="#442141">{title}</Typography>
      {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
    </Box>
  </Box>
);

// ── labelled input ────────────────────────────────────────────
const ControlField = ({ label, name, value, onChange, type = "number", adornment, helperText, select, children }) => (
  <Box>
    <Typography variant="caption" fontWeight={700} color="text.secondary"
      textTransform="uppercase" letterSpacing={0.8} display="block" mb={0.5}>
      {label}
    </Typography>
    <TextField
      select={select}
      fullWidth size="small" type={select ? undefined : type}
      name={name} value={value ?? ""}
      onChange={onChange}
      helperText={helperText}
      InputProps={adornment ? {
        startAdornment: (
          <Box sx={{ mr: 0.5, color: "text.secondary", display: "flex", alignItems: "center" }}>
            {adornment}
          </Box>
        ),
      } : undefined}
      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
    >
      {children}
    </TextField>
  </Box>
);

// ── component ─────────────────────────────────────────────────
function Controls() {
  const { data, isLoading, isError, refetch } = useGetControlsQuery();
  const [updateControls, { isLoading: isSaving }] = useUpdateControlsMutation();

  const [form, setForm] = useState({
    skinAiCharges: "",
    beauticianCommision: "",
    beauticianCommissionGst: "",
    beauticianReferralDiscountForProduct: "",
    beauticianReferralDiscountForServices: "",
    beauticianReferralDiscountType: "percentage",
    gstPercentage: "",
    convenienceCharges: "",
    platformFees: "",
  });

  const [snack, setSnack] = useState({ open: false, msg: "", severity: "success" });

  // populate form when data loads
  useEffect(() => {
    if (data) {
      setForm({
        skinAiCharges:                      data.skinAiCharges ?? "",
        beauticianCommision:                data.beauticianCommision ?? "",
        beauticianCommissionGst:            data.beauticianCommissionGst ?? "",
        beauticianReferralDiscountForProduct:  data.beauticianReferralDiscountForProduct ?? "",
        beauticianReferralDiscountForServices: data.beauticianReferralDiscountForServices ?? "",
        beauticianReferralDiscountType:     data.beauticianReferralDiscountType ?? "percentage",
        gstPercentage:                      data.gstPercentage ?? "",
        convenienceCharges:                 data.convenienceCharges ?? "",
        platformFees:                       data.platformFees ?? "",
      });
    }
  }, [data]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: name === "beauticianReferralDiscountType" ? value : value === "" ? "" : Number(value),
    }));
  };

  const handleSave = async () => {
    try {
      await updateControls(form).unwrap();
      setSnack({ open: true, msg: "All controls updated successfully!", severity: "success" });
      refetch();
    } catch (err) {
      setSnack({ open: true, msg: err?.data?.message || "Update failed. Please try again.", severity: "error" });
    }
  };

  if (isLoading) return (
    <DashboardLayout><DashboardNavbar />
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress sx={{ color: "#442141" }} />
      </Box>
      <Footer />
    </DashboardLayout>
  );

  if (isError) return (
    <DashboardLayout><DashboardNavbar />
      <Box p={3}><Alert severity="error">Failed to load controls. Please refresh.</Alert></Box>
      <Footer />
    </DashboardLayout>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box p={3} pb={10}>

        {/* ── page title ── */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <TuneIcon sx={{ color: "#442141", fontSize: 28 }} />
            <Box>
              <Typography variant="h5" fontWeight={800} color="#442141">Platform Controls</Typography>
              <Typography variant="caption" color="text.secondary">
                Manage all platform-level charges, commissions and discount settings
              </Typography>
            </Box>
          </Box>
          <Chip
            label={data?.updatedAt ? `Last updated: ${new Date(data.updatedAt).toLocaleString()}` : "Not set yet"}
            size="small" variant="outlined"
            sx={{ fontSize: 11, color: "text.secondary" }}
          />
        </Box>

        <Grid container spacing={3}>

          {/* ── SECTION 1 — Skin AI ── */}
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 3, borderRadius: 3, boxShadow: "0 2px 12px rgba(0,0,0,0.07)", height: "100%" }}>
              <SectionHead
                icon={<FaceRetouchingNaturalIcon sx={{ color: "#442141", fontSize: 22 }} />}
                title="Skin AI Charges"
                subtitle="Charge per scan payment (₹1 – ₹100)"
              />
              <ControlField
                label="Skin AI Charges (₹)"
                name="skinAiCharges"
                value={form.skinAiCharges}
                onChange={handleChange}
                adornment={<CurrencyRupeeIcon sx={{ fontSize: 16 }} />}
                helperText="Amount charged per skin AI scan. Min ₹1, Max ₹100."
              />
            </Card>
          </Grid>

          {/* ── SECTION 2 — Platform Fees ── */}
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 3, borderRadius: 3, boxShadow: "0 2px 12px rgba(0,0,0,0.07)", height: "100%" }}>
              <SectionHead
                icon={<CurrencyRupeeIcon sx={{ color: "#442141", fontSize: 22 }} />}
                title="Platform Charges"
                subtitle="Flat amounts added to orders"
              />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <ControlField
                    label="Convenience Charges (₹)"
                    name="convenienceCharges"
                    value={form.convenienceCharges}
                    onChange={handleChange}
                    adornment={<CurrencyRupeeIcon sx={{ fontSize: 16 }} />}
                    helperText="Flat convenience fee per order."
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <ControlField
                    label="Platform Fees (₹)"
                    name="platformFees"
                    value={form.platformFees}
                    onChange={handleChange}
                    adornment={<CurrencyRupeeIcon sx={{ fontSize: 16 }} />}
                    helperText="Flat platform fee per order."
                  />
                </Grid>
              </Grid>
            </Card>
          </Grid>

          {/* ── SECTION 3 — Tax ── */}
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 3, borderRadius: 3, boxShadow: "0 2px 12px rgba(0,0,0,0.07)", height: "100%" }}>
              <SectionHead
                icon={<PercentIcon sx={{ color: "#442141", fontSize: 22 }} />}
                title="Tax Settings"
                subtitle="GST applied as a percentage on orders"
              />
              <ControlField
                label="GST Percentage (%)"
                name="gstPercentage"
                value={form.gstPercentage}
                onChange={handleChange}
                adornment={<PercentIcon sx={{ fontSize: 16 }} />}
                helperText="GST rate applied on taxable order amount. E.g. 18 for 18%."
              />
            </Card>
          </Grid>

          {/* ── SECTION 4 — Beautician Commission ── */}
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 3, borderRadius: 3, boxShadow: "0 2px 12px rgba(0,0,0,0.07)", height: "100%" }}>
              <SectionHead
                icon={<SpaIcon sx={{ color: "#442141", fontSize: 22 }} />}
                title="Beautician Commission"
                subtitle="Commission and GST on beautician earnings"
              />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <ControlField
                    label="Beautician Commission (%)"
                    name="beauticianCommision"
                    value={form.beauticianCommision}
                    onChange={handleChange}
                    adornment={<PercentIcon sx={{ fontSize: 16 }} />}
                    helperText="% of order amount paid to beautician."
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <ControlField
                    label="Commission GST (%)"
                    name="beauticianCommissionGst"
                    value={form.beauticianCommissionGst}
                    onChange={handleChange}
                    adornment={<PercentIcon sx={{ fontSize: 16 }} />}
                    helperText="GST applied on beautician commission."
                  />
                </Grid>
              </Grid>
            </Card>
          </Grid>

          {/* ── SECTION 5 — Referral Discounts ── */}
          <Grid item xs={12}>
            <Card sx={{ p: 3, borderRadius: 3, boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
              <SectionHead
                icon={<TuneIcon sx={{ color: "#442141", fontSize: 22 }} />}
                title="Beautician Referral Discounts"
                subtitle="Discount given to users referred by a beautician"
              />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <ControlField
                    label="Discount Type"
                    name="beauticianReferralDiscountType"
                    value={form.beauticianReferralDiscountType}
                    onChange={handleChange}
                    select
                    helperText="Whether discount is a flat amount or percentage."
                  >
                    <MenuItem value="percentage">Percentage (%)</MenuItem>
                    <MenuItem value="amount">Flat Amount (₹)</MenuItem>
                  </ControlField>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <ControlField
                    label={`Discount on Products (${form.beauticianReferralDiscountType === "percentage" ? "%" : "₹"})`}
                    name="beauticianReferralDiscountForProduct"
                    value={form.beauticianReferralDiscountForProduct}
                    onChange={handleChange}
                    adornment={form.beauticianReferralDiscountType === "percentage"
                      ? <PercentIcon sx={{ fontSize: 16 }} />
                      : <CurrencyRupeeIcon sx={{ fontSize: 16 }} />}
                    helperText="Referral discount applied on product orders."
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <ControlField
                    label={`Discount on Services (${form.beauticianReferralDiscountType === "percentage" ? "%" : "₹"})`}
                    name="beauticianReferralDiscountForServices"
                    value={form.beauticianReferralDiscountForServices}
                    onChange={handleChange}
                    adornment={form.beauticianReferralDiscountType === "percentage"
                      ? <PercentIcon sx={{ fontSize: 16 }} />
                      : <CurrencyRupeeIcon sx={{ fontSize: 16 }} />}
                    helperText="Referral discount applied on service bookings."
                  />
                </Grid>
              </Grid>
            </Card>
          </Grid>

        </Grid>

        {/* ── SUMMARY PREVIEW ── */}
        <Card sx={{ p: 3, borderRadius: 3, mt: 3, boxShadow: "0 2px 12px rgba(0,0,0,0.07)", background: "#fdf4ff" }}>
          <Typography variant="subtitle2" fontWeight={700} color="#442141" mb={2}>Current Values Preview</Typography>
          <Grid container spacing={1.5}>
            {[
              { label: "Skin AI Charges",          value: `₹${form.skinAiCharges || 0}` },
              { label: "Convenience Charges",       value: `₹${form.convenienceCharges || 0}` },
              { label: "Platform Fees",             value: `₹${form.platformFees || 0}` },
              { label: "GST",                       value: `${form.gstPercentage || 0}%` },
              { label: "Beautician Commission",     value: `${form.beauticianCommision || 0}%` },
              { label: "Commission GST",            value: `${form.beauticianCommissionGst || 0}%` },
              { label: "Referral Type",             value: form.beauticianReferralDiscountType },
              { label: "Referral — Products",       value: `${form.beauticianReferralDiscountForProduct || 0}${form.beauticianReferralDiscountType === "percentage" ? "%" : "₹"}` },
              { label: "Referral — Services",       value: `${form.beauticianReferralDiscountForServices || 0}${form.beauticianReferralDiscountType === "percentage" ? "%" : "₹"}` },
            ].map((s, i) => (
              <Grid item xs={6} sm={4} md={2} key={i}>
                <Box sx={{ background: "#fff", borderRadius: 2, p: 1.5, textAlign: "center", border: "1px solid #f3e8ff" }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">{s.label}</Typography>
                  <Typography variant="h6" fontWeight={800} color="#442141">{s.value}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Card>

        {/* ── SAVE BUTTON ── */}
        <Box display="flex" justifyContent="flex-end" mt={3}>
          <Button
            variant="contained"
            size="large"
            startIcon={isSaving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            disabled={isSaving}
            onClick={handleSave}
            sx={{
              background: "#442141", color: "#fff", borderRadius: 2.5,
              px: 4, fontWeight: 700, fontSize: 14,
              "&:hover": { background: "#5a2d57" },
            }}
          >
            {isSaving ? "Saving..." : "Update All Controls"}
          </Button>
        </Box>

      </Box>

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack(p => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack(p => ({ ...p, open: false }))} sx={{ fontWeight: 600 }}>
          {snack.msg}
        </Alert>
      </Snackbar>

      <Footer />
    </DashboardLayout>
  );
}

export default Controls;
